/**
 * Shared HTTP Client with Axios
 * Centralized axios configuration with interceptors
 */
import axios from 'axios';
import { API_CONFIG, getAgentCoreURL, getLambdaURL, getIASBaseURL } from './api.config';
import { isTokenExpired } from '../utils/tokenManager';
import { clearAuthData, getEffectiveUserRole } from '../services/auth.service';

/**
 * Create axios instance for AgentCore API
 */
export const agentCoreClient = axios.create({
  baseURL: getAgentCoreURL(),
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

/**
 * Create axios instance for Lambda REST API
 */
export const lambdaClient = axios.create({
  baseURL: getLambdaURL(),
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS,
});

/**
 * Create axios instance for IAS Auth/User-Management APIs.
 * Uses ias-upmToken + ias-accountId headers (added by shared interceptor).
 */
export const iasClient = axios.create({
  baseURL: getIASBaseURL(),
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    ...API_CONFIG.HEADERS,
    'ias-api-version': '1.0',
  },
});

/**
 * Request interceptor - Add authentication token and handle token expiry
 */
const requestInterceptor = (config) => {
  // Check token expiry before request
  if (isTokenExpired()) {
    clearAuthData();
    window.location.href = '/login';
    return Promise.reject(new Error('Token expired'));
  }

  // Add framework token
  const frameworkToken = localStorage.getItem('frameworkToken');
  if (frameworkToken) {
    config.headers['ias-upmtoken'] = frameworkToken;
  }

  // Add user identity headers derived from the decoded IAS token.
  // These let the backend Lambda enforce RBAC without re-calling the IAS API.
  try {
    const decodedTokenData = localStorage.getItem('decodedTokenData');
    if (decodedTokenData) {
      const tokenData = JSON.parse(decodedTokenData);
      if (tokenData.AccountId) {
        config.headers['ias-accountId'] = tokenData.AccountId;
      }
      // Send email so backend can identify the caller
      const email = tokenData.Email || tokenData.email || localStorage.getItem('userEmail');
      if (email) {
        config.headers['X-User-Email'] = email;
      }
      // Send effective role (supports admin "view as" tabs for dev/testing)
      const role = getEffectiveUserRole() || localStorage.getItem('userRole');
      if (role) {
        config.headers['X-User-Role'] = role;
      }
    }
  } catch (error) {
    // Silent fail — headers are best-effort; backend will reject if missing
  }

  return config;
};

/**
 * Response interceptor - Handle success responses
 */
const responseInterceptor = (response) => response;

/**
 * Error interceptor - Handle errors consistently
 */
const errorInterceptor = (error) => {
  // Handle specific error codes
  if (error.response) {
    switch (error.response.status) {
      case 401:
        console.error('Unauthorized - Token may be invalid');
        clearAuthData();
        window.location.href = '/login';
        break;
      case 403:
        console.error('Forbidden - Access denied');
        break;
      case 404:
        console.error('Resource not found');
        break;
      case 500:
        console.error('Server error');
        break;
      case 503:
        console.error('Service unavailable');
        break;
      default:
        console.error('Request failed');
    }
  } else if (error.request) {
    console.error('No response received from server');
  } else {
    console.error('Error setting up request:', error.message);
  }

  return Promise.reject(error);
};

/**
 * Request interceptor for IAS client — same as shared but without X-User-Email/X-User-Role
 * because the IAS server's CORS policy does not allow those headers.
 */
const iasRequestInterceptor = (config) => {
  if (isTokenExpired()) {
    clearAuthData();
    window.location.href = '/login';
    return Promise.reject(new Error('Token expired'));
  }

  const frameworkToken = localStorage.getItem('frameworkToken');
  if (frameworkToken) {
    config.headers['ias-upmtoken'] = frameworkToken;
  }

  try {
    const decodedTokenData = localStorage.getItem('decodedTokenData');
    if (decodedTokenData) {
      const tokenData = JSON.parse(decodedTokenData);
      if (tokenData.AccountId) {
        config.headers['ias-accountId'] = tokenData.AccountId;
      }
    }
  } catch (error) {
    // Silent fail
  }

  return config;
};

// Apply interceptors — iasClient uses its own request interceptor (no X-User-* headers)
[agentCoreClient, lambdaClient].forEach((client) => {
  client.interceptors.request.use(requestInterceptor, errorInterceptor);
  client.interceptors.response.use(responseInterceptor, errorInterceptor);
});

iasClient.interceptors.request.use(iasRequestInterceptor, errorInterceptor);
iasClient.interceptors.response.use(responseInterceptor, errorInterceptor);

// Export both clients
// Export all clients
const httpClients = {
  agentCore: agentCoreClient,
  lambda: lambdaClient,
  ias: iasClient,
};

export default httpClients;
